import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { DividerModule } from 'primeng/divider';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RRule, Frequency, Weekday, ByWeekday } from 'rrule';
import type { RepeatRuleResponse } from '@fitness-force/models';
import { InputNumberComponent } from '../input-number/input-number.component';

/**
 * Interface for week option in repeat rule
 */
interface WeekOption {
  index: number;
  value: string;
  name: string;
}

/**
 * Interface for month option in repeat rule
 */
interface MonthOption {
  value: number;
  name: string;
}

@Component({
  selector: 'app-rrule',
  templateUrl: './rrule.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    DividerModule,
    ButtonModule,
    RadioButtonModule,
    DatePickerModule,
    InputNumberModule,
    DialogModule,
    SelectModule,
    InputNumberComponent,
    TranslateModule,
    NgClass,
  ],
})
export class RruleComponent implements OnInit, OnDestroy {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  readonly visible = input<boolean>(false);
  readonly showNeverStop = input<boolean>(false);
  readonly rRuleObj = input<RepeatRuleResponse>({} as RepeatRuleResponse);
  readonly minimumDate = input<Date>(new Date());

  readonly emitRRule = output<RepeatRuleResponse>();
  readonly hideRrule = output<boolean>();

  formGroup: UntypedFormGroup;
  localMinimumDate: Date = new Date();

  readonly weekOptions: WeekOption[] = [
    { index: 0, value: 'Sunday', name: 'S' },
    { index: 1, value: 'Monday', name: 'M' },
    { index: 2, value: 'Tuesday', name: 'T' },
    { index: 3, value: 'Wednesday', name: 'W' },
    { index: 4, value: 'Thursday', name: 'T' },
    { index: 5, value: 'Friday', name: 'F' },
    { index: 6, value: 'Saturday', name: 'S' },
  ];

  readonly monthOptions: MonthOption[] = [
    { value: 1, name: 'First' },
    { value: 2, name: 'Second' },
    { value: 3, name: 'Third' },
    { value: 4, name: 'Fourth' },
    { value: 5, name: 'Fifth' },
    { value: 6, name: 'Sixth' },
    { value: 7, name: 'Seventh' },
    { value: 8, name: 'Eighth' },
    { value: 9, name: 'Ninth' },
    { value: 10, name: 'Tenth' },
    { value: 11, name: 'Eleventh' },
    { value: 12, name: 'Twelfth' },
    { value: 13, name: 'Thirteenth' },
    { value: 14, name: 'Fourteenth' },
    { value: 15, name: 'Fifteenth' },
    { value: 16, name: 'Sixteenth' },
    { value: 17, name: 'Seventeenth' },
    { value: 18, name: 'Eighteenth' },
    { value: 19, name: 'Nineteenth' },
    { value: 20, name: 'Twentieth' },
    { value: 21, name: 'Twenty-first' },
    { value: 22, name: 'Twenty-second' },
    { value: 23, name: 'Twenty-third' },
    { value: 24, name: 'Twenty-fourth' },
    { value: 25, name: 'Twenty-fifth' },
    { value: 26, name: 'Twenty-sixth' },
    { value: 27, name: 'Twenty-seventh' },
    { value: 28, name: 'Twenty-eighth' },
    { value: 29, name: 'Twenty-ninth' },
    { value: 30, name: 'Thirtieth' },
    { value: 31, name: 'Thirty-first' },
  ];

  constructor() {
    const date = new Date().setHours(0, 0, 0, 0);
    this.localMinimumDate = new Date(date);

    this.formGroup = this.fb.group({
      repeat: new UntypedFormControl('daily', [Validators.required]),
      interval: new UntypedFormControl('', [Validators.required]),
      until: new UntypedFormControl('', []),
      count: new UntypedFormControl('', []),
      weekDays: new UntypedFormControl([], []),
      monthDays: new UntypedFormControl('', []),
      repeatableRule: new UntypedFormControl('', []),
    });
  }

  ngOnInit(): void {
    this.CreateFormControls();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //#region formcontrol getter and setter

  get Repeat(): string {
    return this.formGroup.controls['repeat'].value;
  }

  set Repeat(value: string) {
    this.formGroup.controls['repeat'].setValue(value);
  }

  get MonthDays(): MonthOption | null {
    return this.formGroup.controls['monthDays'].value;
  }

  get WeekDays(): WeekOption[] {
    return this.formGroup.controls['weekDays'].value || [];
  }

  //#endregion formcontrol getter and setter

  isValidDate(date: unknown): boolean {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  //#region  methods

  CreateFormControls(): void {
    const rRuleObj = this.rRuleObj();
    if (rRuleObj?.RRule) {
      const ruleObj: RRule = RRule.fromString(rRuleObj.RRule);
      const frequency: Frequency = this.GetRRuleFrequency(
        String(Frequency[ruleObj.origOptions.freq || 0]).toLowerCase(),
      );

      this.formGroup.controls['repeat'].setValue(
        String(Frequency[ruleObj.origOptions.freq || -1]).toLowerCase(),
      );
      this.formGroup.controls['interval'].setValue(
        ruleObj.origOptions.interval,
      );

      if (ruleObj.origOptions.count && ruleObj.origOptions.count > 0) {
        this.formGroup.controls['repeatableRule'].setValue('4');
      } else if (
        ruleObj.origOptions.until &&
        this.isValidDate(ruleObj.origOptions.until)
      ) {
        this.formGroup.controls['repeatableRule'].setValue('3');
      } else {
        this.formGroup.controls['repeatableRule'].setValue(
          this.showNeverStop() ? '2' : '1',
        );
      }

      this.formGroup.controls['weekDays'].setValue(
        this.GetWeekDayFromRRuleWeekDays(
          frequency,
          ruleObj.origOptions.byweekday as ByWeekday[],
        ) ?? null,
      );
      this.formGroup.controls['monthDays'].setValue(
        this.monthOptions.find(
          (option: MonthOption) =>
            option.value === ruleObj.origOptions.bymonthday,
        ),
      );
      this.formGroup.controls['until'].setValue(ruleObj.origOptions.until);
      this.formGroup.controls['count'].setValue(ruleObj.origOptions.count);
    } else {
      this.formGroup.controls['repeat'].setValue('daily');
      this.formGroup.controls['interval'].setValue(1);
      this.formGroup.controls['repeatableRule'].setValue(
        this.showNeverStop() ? '2' : '1',
      );
      this.formGroup.controls['weekDays'].setValue(null);
      this.formGroup.controls['monthDays'].setValue(null);
      this.formGroup.controls['count'].setValue(null);
    }

    // Subscribe to form control changes with proper cleanup
    this.formGroup.controls['weekDays'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((weekDays: WeekOption[]) => {
        if (!(weekDays && weekDays.length > 0)) {
          const currentDayOption = this.weekOptions.filter(
            (option: WeekOption) => {
              return option.index === new Date().getDay();
            },
          );
          this.formGroup.controls['weekDays'].setValue(currentDayOption);
        }
      });

    this.formGroup.controls['repeatableRule'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((ruleValue: string) => {
        if (ruleValue === '3') {
          this.formGroup.controls['count'].setValue(null);
          const now = new Date();
          const untilDate = new Date(
            now.getFullYear(),
            now.getMonth() + 6,
            now.getDate(),
          );
          this.formGroup.controls['until'].setValue(untilDate);
        } else if (ruleValue === '4') {
          this.formGroup.controls['until'].setValue(null);
          this.formGroup.controls['count'].setValue(12);
        } else {
          this.formGroup.controls['until'].setValue(null);
          this.formGroup.controls['count'].setValue(null);
        }
      });

    this.formGroup.controls['repeat'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((repeatValue: string) => {
        if (repeatValue === 'weekly') {
          const currentDayOption = this.weekOptions.filter(
            (option: WeekOption) => {
              return option.index === new Date().getDay();
            },
          );
          this.formGroup.controls['weekDays'].setValue(currentDayOption);
          this.formGroup.controls['monthDays'].setValue(null);
        } else if (repeatValue === 'monthly') {
          this.formGroup.controls['weekDays'].setValue(null);
          this.formGroup.controls['monthDays'].setValue(this.monthOptions[0]);
        } else {
          this.formGroup.controls['weekDays'].setValue(null);
          this.formGroup.controls['monthDays'].setValue(null);
        }
      });
  }

  AddOrRemoveWeekDays(weekDay: WeekOption): void {
    const weekDaysValue = this.WeekDays;
    const index: number = weekDaysValue
      .map((item) => item.index)
      .indexOf(weekDay.index);
    if (index === -1) {
      weekDaysValue.push(weekDay);
    } else {
      weekDaysValue.splice(index, 1);
    }
    this.formGroup.controls['weekDays'].setValue(weekDaysValue);
  }

  HideRRule(): void {
    // Emit false to close the dialog
    this.hideRrule.emit(false);
  }

  EmitRepeatRuleData(ruleResponse: RepeatRuleResponse): void {
    this.emitRRule.emit(ruleResponse);
    this.HideRRule();
  }

  CreateRRule(): void {
    const frequency: Frequency = this.GetRRuleFrequency(this.Repeat);
    const until: Date =
      this.formGroup.controls['repeatableRule'].value === '3'
        ? new Date(this.formGroup.controls['until'].value)
        : new Date();

    const monthDays = this.formGroup.controls['monthDays'].value;

    const rule = new RRule({
      freq: frequency,
      interval: 1,
      byweekday: this.GetRRuleWeekDays(frequency),
      bymonthday:
        frequency === Frequency.MONTHLY && monthDays ? monthDays.value : null,
      until:
        this.formGroup.controls['repeatableRule'].value === '3'
          ? new Date(
              Date.UTC(
                until.getFullYear(),
                until.getMonth(),
                until.getDate(),
                until.getHours(),
                until.getMinutes(),
                until.getSeconds(),
              ),
            )
          : null,
      count:
        this.formGroup.controls['repeatableRule'].value === '4'
          ? this.formGroup.controls['count'].value
          : null,
    });

    this.EmitRepeatRuleData({
      RRule: rule.toString(),
      RRuleText: rule.toText(),
    });
  }

  GetRRuleFrequency(repeatValue: string): Frequency {
    if (repeatValue === 'weekly') {
      return RRule.WEEKLY;
    } else if (repeatValue === 'monthly') {
      return RRule.MONTHLY;
    } else {
      return RRule.DAILY;
    }
  }

  GetRRuleWeekDays(frequency: Frequency): Weekday[] | null {
    if (frequency === Frequency.WEEKLY) {
      const weekday: Weekday[] = [];
      this.WeekDays.forEach((weekDayOption: WeekOption) => {
        switch (String(weekDayOption.value).toLowerCase()) {
          case 'sunday':
            weekday.push(RRule.SU);
            break;
          case 'monday':
            weekday.push(RRule.MO);
            break;
          case 'tuesday':
            weekday.push(RRule.TU);
            break;
          case 'wednesday':
            weekday.push(RRule.WE);
            break;
          case 'thursday':
            weekday.push(RRule.TH);
            break;
          case 'friday':
            weekday.push(RRule.FR);
            break;
          case 'saturday':
            weekday.push(RRule.SA);
            break;
          default:
            console.error(
              'Invalid weekday value in GetRRuleWeekDays:',
              JSON.stringify(weekDayOption),
            );
            break;
        }
      });
      return weekday;
    } else {
      return null;
    }
  }

  GetWeekDayFromRRuleWeekDays(
    frequency: Frequency,
    weekDays: ByWeekday[],
  ): WeekOption[] | null {
    if (frequency === Frequency.WEEKLY) {
      const weekday: WeekOption[] = [];
      weekDays.forEach((rruleWeekday: ByWeekday) => {
        const weekdayString = rruleWeekday.toString().toUpperCase();
        let foundOption: WeekOption | undefined;

        switch (weekdayString) {
          case 'SU':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'sunday',
            );
            break;
          case 'MO':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'monday',
            );
            break;
          case 'TU':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'tuesday',
            );
            break;
          case 'WE':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'wednesday',
            );
            break;
          case 'TH':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'thursday',
            );
            break;
          case 'FR':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'friday',
            );
            break;
          case 'SA':
            foundOption = this.weekOptions.find(
              (option: WeekOption) =>
                String(option.value).toLowerCase() === 'saturday',
            );
            break;
          default:
            console.error(
              'Invalid RRule weekday value in GetWeekDayFromRRuleWeekDays:',
              weekdayString,
            );
            break;
        }

        if (foundOption) {
          weekday.push(foundOption);
        }
      });
      return weekday;
    } else {
      return null;
    }
  }

  //#endregion  methods
}
